import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@material-ui/core'

import { CommonStepFields, resolveStepEnterBehavior } from './CommonStepFields'
import {
    PipelineDefinition,
    PipelineStepInstance,
    TransitionEnterBehavior,
} from './pipelineBuilder.types'
import { getCatalogEntry } from './stepCatalog'
import { getStepDisplayLabel } from './stepDisplayUtils'
import { isStepRemovable } from './stepGuards'
import { LeadsStepConfig, ProspectsStepConfig } from './stepConfig.types'
import { getStepConfig } from './stepConfigUtils'
import { getStepPanel } from './stepPanels'
import { summarizeNode, syncEnterBehaviorOnStep } from './transitionUtils'

interface StepEditDialogProps {
    open: boolean
    step: PipelineStepInstance | null
    definition: PipelineDefinition
    apartmentId?: number
    readOnly?: boolean
    onClose: () => void
    onSave: (stepId: string, patch: Partial<PipelineStepInstance>) => void
    onRemove: (stepId: string) => void
}

export const StepEditDialog: React.FC<StepEditDialogProps> = ({
    open,
    step,
    definition,
    apartmentId,
    readOnly = false,
    onClose,
    onSave,
    onRemove,
}) => {
    const [enabled, setEnabled] = useState(true)
    const [enterBehavior, setEnterBehavior] = useState<TransitionEnterBehavior>(
        'auto_start',
    )
    const [config, setConfig] = useState<
        ProspectsStepConfig | LeadsStepConfig | Record<string, unknown>
    >({})

    useEffect(() => {
        if (step) {
            setEnabled(step.enabled)
            setEnterBehavior(resolveStepEnterBehavior(step))
            if (step.type === 'prospects') {
                setConfig(getStepConfig(step, 'prospects'))
            } else if (step.type === 'leads') {
                setConfig(getStepConfig(step, 'leads'))
            } else {
                setConfig(
                    step.config && typeof step.config === 'object'
                        ? { ...step.config }
                        : {},
                )
            }
        }
    }, [step])

    if (!step) {
        return null
    }

    const catalog = getCatalogEntry(step.type)
    const canRemove = isStepRemovable(step.type)
    const StepPanel = getStepPanel(step.type)

    const previewStep = syncEnterBehaviorOnStep(
        {
            ...step,
            config,
            enabled,
        },
        enterBehavior,
    )

    const handlePanelChange = (patch: Partial<PipelineStepInstance>) => {
        if (patch.config) {
            setConfig(
                patch.config as
                    | ProspectsStepConfig
                    | LeadsStepConfig
                    | Record<string, unknown>,
            )
        }
        if (patch.enabled !== undefined) {
            setEnabled(patch.enabled)
        }
        if (patch.enter_behavior) {
            setEnterBehavior(patch.enter_behavior)
        }
    }

    const handleSave = () => {
        const nextStep = syncEnterBehaviorOnStep(
            {
                ...step,
                enabled,
                config,
            },
            enterBehavior,
        )
        onSave(step.id, {
            enabled: nextStep.enabled,
            config: nextStep.config,
            enter_behavior: nextStep.enter_behavior,
            entry: nextStep.entry,
        })
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="div">
                    {getStepDisplayLabel(previewStep)}
                </Typography>
                {step.type === 'leads' && (
                    <Typography variant="body2" color="textSecondary">
                        Roster of prospective tenants
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {step.type !== 'leads' && (
                    <>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            paragraph
                        >
                            {catalog.description}
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom>
                            Summary
                        </Typography>
                        <Typography variant="body2" paragraph>
                            {summarizeNode(previewStep)}
                        </Typography>
                    </>
                )}

                <StepPanel
                    step={previewStep}
                    definition={definition}
                    apartmentId={apartmentId}
                    readOnly={readOnly}
                    onChange={handlePanelChange}
                />

                <Divider style={{ margin: '16px 0' }} />

                <CommonStepFields
                    step={step}
                    enabled={enabled}
                    enterBehavior={enterBehavior}
                    readOnly={readOnly}
                    onEnabledChange={setEnabled}
                    onEnterBehaviorChange={setEnterBehavior}
                />
            </DialogContent>
            <DialogActions>
                {canRemove && !readOnly && (
                    <Button
                        onClick={() => {
                            onRemove(step.id)
                            onClose()
                        }}
                        color="secondary"
                    >
                        Remove step
                    </Button>
                )}
                <Button onClick={onClose}>Cancel</Button>
                {!readOnly && (
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
